import { deepLinks } from '../../utils/deepLinks';
import { navigateTab } from '../../hooks/useTabRouter';
import { colors } from '../../styles/theme';
import styles from '../sections/DealTable.module.css';

/**
 * The "Pending Update" badge on a pipeline board row (badge spec
 * `docs/specs/pipeline-core/update-pending-badge-v2-spec.md`).
 *
 * **One component for both boards, on purpose.** v1's table and v2's are deliberate duplicates that
 * diverge until v1 is deleted at the cut — but this badge must NOT diverge: it is the same affordance
 * pointing at the same review surface, and the two boards run side by side during parallel-run, where
 * a visual or link difference would read as a data difference. It borrows `DealTable.module.css` for
 * the same reason both tables already share it.
 *
 * Three states, and the middle one is the point: **disabled, not hidden.** A viewer who can see the
 * row can see that updates are pending on it; only acting on them is gated (v1's semantics, carried
 * verbatim including the tooltip). Hiding the badge instead would tell a viewer nothing is pending.
 */
export interface PendingUpdateBadgeProps {
  /** For the aria-label — "Review pending update for <name>". */
  dealName: string;
  /** The CRM list the record lives on. Null → no panel to open (v1 only; see `unresolvedTitle`). */
  listId: number | null | undefined;
  /** The `crm_list_entry` id — the record the panel opens on. */
  entryId: number | null | undefined;
  /** The viewer's actionable set is non-empty (the per-field evidence gate). */
  clickable: boolean;
}

const badgeStyle = {
  background: colors.badge.pendingUpdate.bg,
  color: colors.badge.pendingUpdate.color,
  border: `1px solid ${colors.badge.pendingUpdate.border}`,
};

/** v1's tooltip, carried verbatim — the gate it describes is the same one. */
const NOT_ON_AUDIENCE = "Pending update exists; you are not on this deal's update audience.";
/**
 * v1 only. Phase B can count a pending update from `deal_suggestions` while the CRM dual-write found
 * no list entry for the opp (never imported, or a retired candidate). Saying so is better than a link
 * into an empty panel, and better than borrowing the audience tooltip, which would name the wrong
 * reason.
 */
const NO_RECORD = 'Pending update exists; this deal has no CRM record to open yet.';

export function PendingUpdateBadge({ dealName, listId, entryId, clickable }: PendingUpdateBadgeProps) {
  const resolved = listId != null && entryId != null;

  if (!resolved || !clickable) {
    return (
      <span
        className={`${styles.badge} ${styles.updateBadge} ${styles.updateBadgeDisabled}`}
        style={badgeStyle}
        title={resolved ? NOT_ON_AUDIENCE : NO_RECORD}
      >
        Pending Update
      </span>
    );
  }

  const href = deepLinks.crmRecord(listId, entryId);
  return (
    <a
      href={href}
      className={`${styles.badge} ${styles.updateBadge}`}
      style={badgeStyle}
      title="Pending update is ready for review"
      aria-label={`Review pending update for ${dealName}`}
      onClick={(e) => {
        // stopPropagation: the row itself is clickable (expand), and a badge click is not a row click.
        e.stopPropagation();
        e.preventDefault();
        // Cross-tab navigation goes through the shared router, which drops the origin tab's params and
        // sets only these two — never a hand-rolled pushState (frontend rules / deep-link spec §3.4).
        navigateTab('crm', { params: { sel: `list:${listId}`, rec: String(entryId) } });
      }}
    >
      Pending Update
    </a>
  );
}
